import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, BookOpen, FileText, DollarSign, Calendar, AlertTriangle, CheckCircle, Info, Lightbulb } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TaxGuide = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basics');

  const taxGuideContent = {
    basics: {
      title: "Tax Preparation Basics",
      icon: BookOpen,
      content: [
        {
          section: "Understanding Your Tax Obligations",
          items: [
            "Every U.S. citizen and resident must file a tax return if their income exceeds certain thresholds",
            "Tax year runs from January 1 to December 31",
            "Filing deadline is typically April 15 (or the next business day if it falls on a weekend/holiday)",
            "Extensions are available but only extend the filing deadline, not the payment deadline"
          ]
        },
        {
          section: "Types of Income",
          items: [
            "W-2 Income: Wages from employers with taxes withheld",
            "1099 Income: Independent contractor income, freelance work, side gigs",
            "Business Income: Profits from sole proprietorships, partnerships, or corporations",
            "Investment Income: Capital gains, dividends, interest",
            "Other Income: Rental income, retirement distributions, unemployment benefits"
          ]
        },
        {
          section: "Standard vs. Itemized Deductions",
          items: [
            "Standard Deduction 2024: $14,600 (single), $29,200 (married filing jointly)",
            "Itemize if your deductions exceed the standard deduction amount",
            "Common itemized deductions: mortgage interest, state/local taxes, charitable contributions, medical expenses",
            "Keep detailed records and receipts for all claimed deductions"
          ]
        }
      ]
    },
    business: {
      title: "Business Expense Deductions",
      icon: DollarSign,
      content: [
        {
          section: "Schedule C Overview",
          items: [
            "Schedule C is used to report income and expenses from sole proprietorships",
            "All business income must be reported, regardless of whether you received a 1099",
            "Business expenses must be ordinary and necessary for your trade or business",
            "Keep detailed records of all business transactions throughout the year"
          ]
        },
        {
          section: "Common Business Deductions",
          items: [
            "Office Supplies: Paper, pens, software, computer equipment",
            "Professional Services: Legal fees, accounting, consulting",
            "Travel Expenses: Business trips, hotels, airfare (not commuting)",
            "Meals: 50% of business meal costs (100% during 2021-2022 for restaurants)",
            "Vehicle Expenses: Either actual costs or standard mileage rate",
            "Home Office: Portion of home used exclusively for business",
            "Marketing: Advertising, website costs, business cards",
            "Equipment: Computers, machinery, tools (may need to be depreciated)"
          ]
        },
        {
          section: "Record Keeping Best Practices",
          items: [
            "Keep receipts for all business expenses, no matter how small",
            "Use a dedicated business bank account and credit card",
            "Maintain a mileage log for vehicle deductions",
            "Document the business purpose of each expense",
            "Store records for at least 3 years (7 years for certain situations)",
            "Use accounting software or apps to track expenses throughout the year"
          ]
        }
      ]
    },
    deadlines: {
      title: "Important Tax Deadlines",
      icon: Calendar,
      content: [
        {
          section: "Annual Filing Deadlines",
          items: [
            "April 15: Individual tax returns (Form 1040) due",
            "March 15: S-Corp and partnership returns due",
            "April 15: C-Corp returns due (if fiscal year ends December 31)",
            "January 31: W-2 and 1099 forms must be provided to recipients",
            "February 28/March 31: Information returns filed with IRS"
          ]
        },
        {
          section: "Quarterly Estimated Tax Payments",
          items: [
            "Q1: January 15 (for October-December of previous year)",
            "Q2: April 15 (for January-March)",
            "Q3: June 15 (for April-May)",
            "Q4: September 15 (for June-August)",
            "Final Payment: January 15 (for September-December)"
          ]
        },
        {
          section: "Extension Deadlines",
          items: [
            "Individual Extensions: October 15 (6-month extension)",
            "Business Extensions: September 15 (6-month extension for most entities)",
            "Remember: Extensions extend filing time, not payment time",
            "Pay estimated taxes owed by original deadline to avoid penalties"
          ]
        }
      ]
    },
    tips: {
      title: "Tax Planning Tips",
      icon: Lightbulb,
      content: [
        {
          section: "Year-Round Tax Strategies",
          items: [
            "Set aside 25-30% of self-employment income for taxes",
            "Make quarterly estimated tax payments to avoid underpayment penalties",
            "Keep business and personal expenses completely separate",
            "Consider retirement account contributions to reduce taxable income",
            "Time large business purchases strategically (Section 179 deduction)",
            "Review and adjust tax withholdings from W-2 jobs"
          ]
        },
        {
          section: "End-of-Year Planning",
          items: [
            "Accelerate business expenses into the current tax year if beneficial",
            "Defer income to the following year if it makes sense",
            "Make charitable contributions before December 31",
            "Review investment portfolios for tax-loss harvesting opportunities",
            "Consider Roth IRA conversions in low-income years",
            "Organize all tax documents and receipts for easy filing"
          ]
        },
        {
          section: "Common Tax Mistakes to Avoid",
          items: [
            "Don't mix business and personal expenses",
            "Don't forget to report all income sources",
            "Don't claim personal expenses as business deductions",
            "Don't wait until April to organize tax documents",
            "Don't ignore estimated tax payment requirements",
            "Don't file without reviewing for accuracy and completeness"
          ]
        }
      ]
    }
  };

  const currentContent = taxGuideContent[activeTab];
  const IconComponent = currentContent.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <BookOpen className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Tax Preparation Guide
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Comprehensive guide to tax preparation, deductions, and best practices
          </p>
        </div>

        {/* Important Notice */}
        <Card className="shadow-xl border-2 border-orange-200 bg-orange-50 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h3 className="font-semibold text-orange-800 mb-2">Important Disclaimer</h3>
                <p className="text-orange-700">
                  This guide provides general tax information and should not be considered professional tax advice. 
                  Tax laws change frequently and individual situations vary. Always consult with a qualified tax professional 
                  or CPA for advice specific to your situation before making tax decisions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation Tabs */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 sticky top-24">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="text-slate-800">Guide Sections</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {Object.entries(taxGuideContent).map(([key, section]) => {
                    const SectionIcon = section.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`w-full text-left p-4 flex items-center gap-3 transition-colors ${
                          activeTab === key
                            ? 'bg-blue-100 text-blue-800 border-r-2 border-blue-600'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <SectionIcon className="w-5 h-5" />
                        <span className="font-medium">{section.title}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg border-0">
              <CardHeader className="bg-blue-50 border-b">
                <CardTitle className="flex items-center gap-3 text-blue-800">
                  <IconComponent className="w-6 h-6" />
                  {currentContent.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-8">
                  {currentContent.content.map((section, index) => (
                    <div key={index}>
                      <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <Info className="w-5 h-5 text-blue-600" />
                        {section.section}
                      </h3>
                      <div className="space-y-3">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <p className="text-slate-700">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tax Helper Pros CTA */}
        <Card className="shadow-xl border-2 border-blue-200 bg-blue-50 mt-12">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Calculator className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-blue-800">Ready to Simplify Your Tax Preparation?</h3>
            </div>
            <p className="text-blue-700 text-lg mb-6">
              Tax Helper Pros automatically categorizes your business expenses and generates tax-ready reports. 
              Upload your bank statements and let our AI do the heavy lifting.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
              >
                Get Started Today
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3"
              >
                View Pricing
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Resources */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/help')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Help Center</h3>
              <p className="text-slate-600 text-sm">Get answers to frequently asked questions</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/contact')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Contact Support</h3>
              <p className="text-slate-600 text-sm">Get personalized help from our team</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => window.open('https://www.irs.gov', '_blank')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">IRS Resources</h3>
              <p className="text-slate-600 text-sm">Official IRS publications and forms</p>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaxGuide;